import React, { useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2,
  Wallet,
  Eye,
  EyeOff,
  Lock,
  Mail,
  AlertCircle,
  ShieldCheck,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n";
import { formatApiError } from "../lib/api";

/**
 * JMS ERP · Login page (UI-only redesign — auth logic is 100% preserved).
 * Toda a interação com AuthContext.login() e a navegação pós-login permanecem idênticas.
 */
export default function Login() {
  const { user, loading, login } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false); // UI-only (sem lógica)
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#F8FAFC]">
        <Loader2 className="h-6 w-6 text-primary animate-spin" />
      </div>
    );
  }
  if (user) return <Navigate to={user.must_change_password ? "/change-password" : "/dashboard"} replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const u = await login(email.trim().toLowerCase(), password);
      toast.success(`${t("app_name")} ✓`);
      if (u?.must_change_password) navigate("/change-password", { replace: true });
      else navigate("/dashboard", { replace: true });
    } catch (err) {
      const raw = formatApiError(err);
      // Mensagem amigável — nunca expõe detalhes do backend.
      const friendly = "E-mail ou senha incorretos. Verifique os dados e tente novamente.";
      setError(friendly);
      toast.error(raw && raw.length < 80 ? raw : friendly);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="login-page"
      className="relative min-h-screen w-full overflow-hidden bg-[#F8FAFC] flex items-center justify-center px-4 py-8 sm:px-6"
    >
      {/* Background — soft geometric SaaS style */}
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
        {/* radial gradient wash */}
        <div className="absolute inset-0 bg-[radial-gradient(60%_50%_at_50%_0%,rgba(22,163,74,0.08),transparent_65%)]" />
        {/* blur circles */}
        <div className="absolute -top-32 -left-24 h-[360px] w-[360px] rounded-full bg-primary/15 blur-3xl opacity-70" />
        <div className="absolute top-1/3 -right-32 h-[420px] w-[420px] rounded-full bg-emerald-200/40 blur-3xl opacity-60" />
        <div className="absolute -bottom-32 left-1/3 h-[320px] w-[320px] rounded-full bg-primary/10 blur-3xl opacity-70" />
        {/* subtle grid */}
        <div
          className="absolute inset-0 opacity-[0.35]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(15,23,42,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(15,23,42,0.04) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
            maskImage:
              "radial-gradient(ellipse 70% 60% at 50% 40%, black 55%, transparent 100%)",
            WebkitMaskImage:
              "radial-gradient(ellipse 70% 60% at 50% 40%, black 55%, transparent 100%)",
          }}
        />
      </div>

      <div className="relative z-10 w-full max-w-[480px] fade-up">
        {/* Card */}
        <div
          data-testid="login-card"
          className="bg-white rounded-[20px] shadow-[0_20px_60px_-15px_rgba(15,23,42,0.15),0_8px_20px_-8px_rgba(15,23,42,0.08)] ring-1 ring-slate-200/60 p-10 sm:p-10"
        >
          {/* Header */}
          <div className="flex flex-col items-center text-center mb-8">
            <div
              aria-hidden="true"
              className="h-14 w-14 rounded-2xl grid place-items-center bg-gradient-to-br from-primary to-emerald-600 shadow-[0_10px_25px_-8px_rgba(22,163,74,0.55)] mb-4"
            >
              <Wallet className="h-7 w-7 text-white" strokeWidth={2.2} />
            </div>
            <h1
              data-testid="login-brand"
              className="font-display font-bold text-2xl tracking-tight text-slate-900"
            >
              JMS ERP
            </h1>
            <h2 className="mt-3 font-display text-[22px] sm:text-2xl font-semibold text-slate-900 tracking-tight">
              Bem-vindo de volta!
            </h2>
            <p className="mt-2 text-sm text-slate-500 leading-relaxed max-w-[360px]">
              Gerencie clientes, processos, pagamentos, documentos e serviços em um só lugar.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                E-mail
              </Label>
              <div className="relative">
                <Mail
                  aria-hidden="true"
                  className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400"
                />
                <Input
                  id="email"
                  data-testid="login-email-input"
                  type="email"
                  autoComplete="email"
                  spellCheck={false}
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seu@email.com"
                  aria-label="E-mail"
                  className="h-[52px] pl-11 pr-4 rounded-xl border-slate-200 bg-slate-50/60 text-slate-900 placeholder:text-slate-400 focus-visible:bg-white focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/20 transition-all duration-200"
                />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                Senha
              </Label>
              <div className="relative">
                <Lock
                  aria-hidden="true"
                  className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400"
                />
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Digite sua senha"
                  aria-label="Senha"
                  className="h-[52px] pl-11 pr-12 rounded-xl border-slate-200 bg-slate-50/60 text-slate-900 placeholder:text-slate-400 focus-visible:bg-white focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/20 transition-all duration-200"
                />
                <button
                  type="button"
                  data-testid="login-password-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                  aria-pressed={showPassword}
                  className="absolute right-3 top-1/2 -translate-y-1/2 h-9 w-9 grid place-items-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 transition-colors"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* Remember me + Forgot */}
            <div className="flex items-center justify-between pt-1">
              <label
                htmlFor="remember-me"
                className="flex items-center gap-2 cursor-pointer select-none text-sm text-slate-600 hover:text-slate-900 transition-colors"
              >
                <Checkbox
                  id="remember-me"
                  data-testid="login-remember-checkbox"
                  checked={rememberMe}
                  onCheckedChange={(v) => setRememberMe(Boolean(v))}
                  className="h-4 w-4 rounded border-slate-300"
                />
                <span>Lembrar-me</span>
              </label>
              <Link
                to="/forgot-password"
                data-testid="login-forgot-link"
                className="text-sm font-medium text-primary hover:text-primary/80 hover:underline underline-offset-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 rounded-sm transition-colors"
              >
                Esqueci minha senha
              </Link>
            </div>

            {/* Error */}
            {error && (
              <div
                role="alert"
                data-testid="login-error"
                className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 fade-up"
              >
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0 text-red-500" aria-hidden="true" />
                <div className="leading-snug">
                  <strong className="font-semibold">E-mail ou senha incorretos.</strong>
                  <div className="text-red-600/90 mt-0.5">Verifique os dados e tente novamente.</div>
                </div>
              </div>
            )}

            {/* Submit */}
            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={submitting}
              className="w-full h-[54px] rounded-xl bg-primary hover:bg-primary/90 active:scale-[0.985] text-primary-foreground font-semibold text-[15px] tracking-tight shadow-[0_10px_24px_-10px_rgba(22,163,74,0.6)] hover:shadow-[0_14px_30px_-10px_rgba(22,163,74,0.7)] transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:shadow-[0_10px_24px_-10px_rgba(22,163,74,0.6)]"
            >
              {submitting ? (
                <span className="inline-flex items-center">
                  <Loader2 className="h-4 w-4 animate-spin mr-2" aria-hidden="true" />
                  Entrando...
                </span>
              ) : (
                "Entrar"
              )}
            </Button>
          </form>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-slate-100 flex flex-col items-center text-center gap-1">
            <div
              data-testid="login-secure-badge"
              className="inline-flex items-center gap-1.5 text-[11px] font-medium text-emerald-700"
            >
              <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
              Ambiente Seguro
            </div>
            <p className="text-[11px] text-slate-400">© 2026 JMS MULTI SERVICE LTDA</p>
            <p className="text-[11px] text-slate-400">Versão 1.0</p>
          </div>
        </div>
      </div>
    </div>
  );
}
